import React, { useEffect, useState } from 'react';
import { ScrollText, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const PETITION_STORAGE_KEY = 'standwithjamaica-petition-signatures-v1';
const PETITION_SELECTED_SIGNATURE_KEY = 'standwithjamaica-petition-selected-signature-v1';
const PETITION_API_ENDPOINT = '/.netlify/functions/petition-signatures';

const displacementPattern = [
  { x: 0, y: 0, r: -2 },
  { x: 10, y: -4, r: 1 },
  { x: -8, y: 6, r: -1 },
  { x: 6, y: 8, r: 2 },
  { x: -6, y: -5, r: -2 },
  { x: 12, y: 4, r: 1 },
  { x: -10, y: 7, r: -1 },
  { x: 4, y: -7, r: 2 }
];

const titleCaseWords = (value) => {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getNamePartsFromDisplayName = (displayName) => {
  const match = String(displayName || '').trim().match(/^(.+)\s+([A-Za-z])\.?$/);
  if (!match) {
    return { firstName: titleCaseWords(String(displayName || '')), lastInitial: '' };
  }

  return {
    firstName: titleCaseWords(match[1]),
    lastInitial: match[2].toUpperCase()
  };
};

const formatDobForDisplay = (value) => {
  if (!value) {
    return 'Not provided';
  }

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return value;
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const safeMonthName = monthNames[month - 1] || String(month);
  return `${safeMonthName} ${day}, ${year}`;
};

const normalizeSignature = (entry, index) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const nameParts = getNamePartsFromDisplayName(entry.displayName);
  const firstNameValue = titleCaseWords(
    typeof entry.firstName === 'string' && entry.firstName.trim()
      ? entry.firstName
      : nameParts.firstName
  );
  const lastInitialValue = (
    typeof entry.lastInitial === 'string' && entry.lastInitial.trim()
      ? entry.lastInitial
      : nameParts.lastInitial
  )
    .charAt(0)
    .toUpperCase();
  const dateOfBirthValue = typeof entry.dateOfBirth === 'string' ? entry.dateOfBirth : '';

  if (!firstNameValue || !lastInitialValue || !dateOfBirthValue) {
    return null;
  }

  return {
    id: typeof entry.id === 'string' && entry.id ? entry.id : `legacy-${index}`,
    firstName: firstNameValue,
    lastInitial: lastInitialValue,
    dateOfBirth: dateOfBirthValue,
    displayName: `${firstNameValue} ${lastInitialValue}.`,
    signedAt: typeof entry.signedAt === 'string' ? entry.signedAt : new Date().toISOString()
  };
};

const signatureKey = (signature) => {
  return [
    String(signature.firstName || '').toLowerCase(),
    String(signature.lastInitial || '').toUpperCase(),
    String(signature.dateOfBirth || '')
  ].join('|');
};

const dedupeSignatures = (list) => {
  const signatureMap = new Map();

  list.forEach((entry, index) => {
    const normalized = normalizeSignature(entry, index);
    if (!normalized) {
      return;
    }

    const key = signatureKey(normalized);
    const existing = signatureMap.get(key);
    if (!existing) {
      signatureMap.set(key, normalized);
      return;
    }

    const existingTime = new Date(existing.signedAt).getTime() || 0;
    const newTime = new Date(normalized.signedAt).getTime() || 0;
    if (newTime >= existingTime) {
      signatureMap.set(key, normalized);
    }
  });

  return Array.from(signatureMap.values()).sort((a, b) => {
    const aTime = new Date(a.signedAt).getTime() || 0;
    const bTime = new Date(b.signedAt).getTime() || 0;
    return aTime - bTime;
  });
};

const hasSameSignatureSet = (left, right) => {
  if (left.length !== right.length) {
    return false;
  }

  const leftKeys = left.map(signatureKey).sort();
  const rightKeys = right.map(signatureKey).sort();
  return leftKeys.every((value, index) => value === rightKeys[index]);
};

const loadLocalSignatures = () => {
  try {
    const storedValue = localStorage.getItem(PETITION_STORAGE_KEY);
    if (!storedValue) {
      return [];
    }

    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return dedupeSignatures(parsed);
  } catch {
    return [];
  }
};

const saveLocalSignatures = (signatures) => {
  localStorage.setItem(PETITION_STORAGE_KEY, JSON.stringify(signatures));
};

const fetchSharedSignatures = async () => {
  const response = await fetch(PETITION_API_ENDPOINT, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Unable to load shared petition signatures.');
  }

  const payload = await response.json();
  const signatures = Array.isArray(payload.signatures) ? payload.signatures : [];
  return dedupeSignatures(signatures);
};

const syncSharedSignatures = async (signatures) => {
  const response = await fetch(PETITION_API_ENDPOINT, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signatures })
  });

  if (!response.ok) {
    throw new Error('Unable to sync signatures.');
  }

  const payload = await response.json();
  const updated = Array.isArray(payload.signatures) ? payload.signatures : signatures;
  return dedupeSignatures(updated);
};

const pushSharedSignature = async (signature) => {
  const response = await fetch(PETITION_API_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signature })
  });

  if (!response.ok) {
    throw new Error('Unable to save signature remotely.');
  }

  const payload = await response.json();
  const signatures = Array.isArray(payload.signatures) ? payload.signatures : [];
  return dedupeSignatures(signatures);
};

const PetitionSection = () => {
  const [firstName, setFirstName] = useState('');
  const [lastInitial, setLastInitial] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [signatures, setSignatures] = useState([]);
  const [selectedSignatureId, setSelectedSignatureId] = useState(null);

  useEffect(() => {
    const localSignatures = loadLocalSignatures();
    const storedSelectedId = localStorage.getItem(PETITION_SELECTED_SIGNATURE_KEY);

    setSignatures(localSignatures);
    if (storedSelectedId && localSignatures.some((signature) => signature.id === storedSelectedId)) {
      setSelectedSignatureId(storedSelectedId);
    }

    let isMounted = true;

    const pullLatestSignatures = async () => {
      try {
        const remoteSignatures = await fetchSharedSignatures();
        const latestLocal = loadLocalSignatures();
        const mergedSignatures = dedupeSignatures([...remoteSignatures, ...latestLocal]);

        if (!isMounted) {
          return;
        }

        setSignatures((previous) => {
          if (hasSameSignatureSet(previous, mergedSignatures)) {
            return previous;
          }
          return mergedSignatures;
        });

        saveLocalSignatures(mergedSignatures);

        if (!hasSameSignatureSet(remoteSignatures, mergedSignatures)) {
          await syncSharedSignatures(mergedSignatures);
        }
      } catch {
        // If remote storage is unavailable, local signatures still remain visible.
      }
    };

    pullLatestSignatures();
    const refreshInterval = window.setInterval(pullLatestSignatures, 20000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    saveLocalSignatures(signatures);
  }, [signatures]);

  useEffect(() => {
    if (selectedSignatureId) {
      localStorage.setItem(PETITION_SELECTED_SIGNATURE_KEY, selectedSignatureId);
    } else {
      localStorage.removeItem(PETITION_SELECTED_SIGNATURE_KEY);
    }
  }, [selectedSignatureId]);

  useEffect(() => {
    if (!selectedSignatureId) {
      return;
    }

    const hasSelectedSignature = signatures.some((signature) => signature.id === selectedSignatureId);
    if (!hasSelectedSignature) {
      setSelectedSignatureId(null);
    }
  }, [signatures, selectedSignatureId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const formattedFirstName = titleCaseWords(firstName);
    const formattedLastInitial = lastInitial.trim().charAt(0).toUpperCase();

    if (!formattedFirstName) {
      setError('Please enter your full first name.');
      return;
    }

    if (!formattedLastInitial) {
      setError('Please enter your last name initial.');
      return;
    }

    if (!dateOfBirth) {
      setError('Please enter your date of birth.');
      return;
    }

    const today = new Date();
    const selectedDate = new Date(dateOfBirth);
    if (selectedDate > today) {
      setError('Date of birth cannot be in the future.');
      return;
    }

    const displayName = `${formattedFirstName} ${formattedLastInitial}.`;
    const newSignature = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      firstName: formattedFirstName,
      lastInitial: formattedLastInitial,
      dateOfBirth,
      displayName,
      signedAt: new Date().toISOString()
    };

    const optimisticSignatures = dedupeSignatures([...signatures, newSignature]);
    setSignatures(optimisticSignatures);
    setSelectedSignatureId(null);

    setSuccess(`${displayName} has signed the petition.`);
    setFirstName('');
    setLastInitial('');
    setDateOfBirth('');

    try {
      const sharedSignatures = await pushSharedSignature(newSignature);
      const mergedSignatures = dedupeSignatures([...optimisticSignatures, ...sharedSignatures]);
      setSignatures(mergedSignatures);
    } catch {
      setError('Saved on this browser. We will sync it to all browsers as soon as connectivity is available.');
    }
  };

  const selectedSignature =
    signatures.find((signature) => signature.id === selectedSignatureId) || null;

  return (
    <section id="petition" className="py-24 bg-white scroll-mt-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm uppercase tracking-wider bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full mb-5">
            <ScrollText className="w-4 h-4" />
            Petition For Change
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-800 mb-5">
            By The People, For The People
          </h2>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Add your name to demand fair wages, accountable leadership, and better living conditions for Jamaican workers and families.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 bg-stone-50 border border-stone-200 rounded-3xl p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-stone-800 mb-2">Sign The Petition</h3>
            <p className="text-stone-600 mb-6 text-sm">
              Your public display will show only your first name and last initial.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="petition-first-name" className="text-sm font-medium text-stone-700">
                  Full First Name
                </label>
                <Input
                  id="petition-first-name"
                  type="text"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="petition-last-initial" className="text-sm font-medium text-stone-700">
                  Last Name Initial
                </label>
                <Input
                  id="petition-last-initial"
                  type="text"
                  value={lastInitial}
                  onChange={(event) => {
                    const nextValue = event.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase();
                    setLastInitial(nextValue);
                  }}
                  placeholder="Example: B"
                  maxLength={1}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="petition-dob" className="text-sm font-medium text-stone-700">
                  Date Of Birth
                </label>
                <Input
                  id="petition-dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(event) => setDateOfBirth(event.target.value)}
                  required
                />
              </div>

              {error && <p className="text-sm text-rose-600">{error}</p>}
              {success && <p className="text-sm text-emerald-600">{success}</p>}

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Complete/Sign Petition
              </Button>
            </form>
          </div>

          <div className="lg:col-span-3 bg-gradient-to-br from-emerald-50 via-white to-amber-50 border border-emerald-100 rounded-3xl p-6 sm:p-8 min-h-[360px]">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h3 className="text-2xl font-bold text-stone-800">Petition Wall</h3>
              <span className="inline-flex items-center rounded-full bg-white border border-stone-200 px-3 py-1 text-sm font-medium text-stone-600">
                {signatures.length} Signatures
              </span>
            </div>

            {selectedSignature && (
              <div className="mb-5 rounded-2xl bg-white border border-stone-200 p-4">
                <div className="text-sm text-stone-700 space-y-1">
                  <p>
                    <span className="font-semibold text-stone-900">Name:</span>{' '}
                    {selectedSignature.firstName} {selectedSignature.lastInitial}.
                  </p>
                  <p>
                    <span className="font-semibold text-stone-900">Date Of Birth:</span>{' '}
                    {formatDobForDisplay(selectedSignature.dateOfBirth)}
                  </p>
                </div>
              </div>
            )}

            {signatures.length === 0 ? (
              <div className="h-[260px] rounded-2xl border border-dashed border-stone-300 bg-white/80 flex items-center justify-center text-center px-6">
                <p className="text-stone-500">
                  Be the first to sign. Your name bubble will appear here as <span className="font-semibold text-stone-700">FirstName L.</span>
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 items-start content-start min-h-[260px]">
                {signatures.map((signature, index) => {
                  const offset = displacementPattern[index % displacementPattern.length];
                  const isSelected = signature.id === selectedSignatureId;

                  return (
                    <button
                      type="button"
                      key={signature.id}
                      onClick={() => {
                        setSelectedSignatureId((previous) =>
                          previous === signature.id ? null : signature.id
                        );
                      }}
                      className={`inline-flex items-center rounded-full border shadow-sm px-4 py-2 text-sm font-semibold transition-all ${
                        isSelected
                          ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                          : 'bg-white border-stone-200 text-stone-700 hover:border-emerald-200'
                      }`}
                      style={{
                        transform: `translate(${offset.x}px, ${offset.y}px) rotate(${offset.r}deg)`
                      }}
                    >
                      {signature.displayName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PetitionSection;
